<template>
  <div v-if="steps && steps.length > 0" class="progress-tracker-container">
    <!-- Collapsed View: Successful completion -->
    <div
      v-if="provisioningState === 'completed' && isCollapsed"
      class="card shadow-sm border-success bg-success-subtle-custom"
    >
      <div class="card-body py-3 px-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div class="d-flex align-items-center text-success">
          <i class="bi bi-check-circle-fill fs-5 me-2"></i>
          <span class="fw-semibold">
            {{ completedMessage }}
          </span>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 toggle-btn"
          @click="toggleCollapse"
          aria-label="Toggle details"
        >
          <span>Show details</span>
          <i class="bi bi-chevron-down"></i>
        </button>
      </div>
    </div>

    <!-- Expanded / Active / Failed View -->
    <div
      v-else
      class="card shadow-sm border-0 progress-tracker-card"
      :class="{
        'border-start border-4 border-danger': provisioningState === 'failed',
        'border-start border-4 border-primary': provisioningState === 'in_progress',
        'border-start border-4 border-success': provisioningState === 'completed'
      }"
    >
      <!-- Card Header -->
      <div
        class="card-header d-flex align-items-center justify-content-between py-3 px-4 border-0"
        :class="{
          'bg-danger bg-opacity-10 text-danger': provisioningState === 'failed',
          'bg-primary bg-opacity-10 text-primary': provisioningState === 'in_progress',
          'bg-light text-dark': provisioningState !== 'failed' && provisioningState !== 'in_progress'
        }"
      >
        <div class="d-flex align-items-center gap-2">
          <!-- Spinner for active operation -->
          <div
            v-if="provisioningState === 'in_progress'"
            class="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span class="visually-hidden">In progress...</span>
          </div>
          <!-- Alert icon for failed operation -->
          <i
            v-else-if="provisioningState === 'failed'"
            class="bi bi-exclamation-triangle-fill text-danger fs-5"
          ></i>
          <!-- Check icon for completed -->
          <i
            v-else-if="provisioningState === 'completed'"
            class="bi bi-check-circle-fill text-success fs-5"
          ></i>
          <!-- Default info icon -->
          <i v-else class="bi bi-list-check text-muted fs-5"></i>

          <h6 class="mb-0 fw-bold header-title">
            {{ operationHeaderTitle }}
          </h6>
        </div>

        <button
          v-if="provisioningState === 'completed'"
          type="button"
          class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 toggle-btn"
          @click="toggleCollapse"
          aria-label="Toggle details"
        >
          <span>Hide details</span>
          <i class="bi bi-chevron-up"></i>
        </button>
      </div>

      <!-- Card Body / Vertical Timeline -->
      <div class="card-body px-4 py-3">
        <div class="timeline">
          <div
            v-for="(step, index) in steps"
            :key="step.id || index"
            class="timeline-item position-relative"
            :class="{
              'is-in-progress': step.status === 'in_progress',
              'is-failed': step.status === 'failed',
              'is-completed': step.status === 'completed',
              'is-skipped': step.status === 'skipped',
              'is-pending': step.status === 'pending',
              'is-last': index === steps.length - 1
            }"
          >
            <!-- Timeline Icon -->
            <div class="timeline-icon-wrapper">
              <span
                v-if="step.status === 'in_progress'"
                class="timeline-spinner spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span class="visually-hidden">Loading...</span>
              </span>
              <i
                v-else-if="step.status === 'completed'"
                class="bi bi-check-circle-fill text-success timeline-icon"
              ></i>
              <i
                v-else-if="step.status === 'failed'"
                class="bi bi-x-circle-fill text-danger timeline-icon"
              ></i>
              <i
                v-else-if="step.status === 'skipped'"
                class="bi bi-dash-circle text-muted timeline-icon"
              ></i>
              <i
                v-else
                class="bi bi-circle text-muted timeline-icon"
              ></i>
            </div>

            <!-- Step Content -->
            <div class="timeline-content pb-3">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span
                  class="step-label"
                  :class="{
                    'fw-bold text-primary active-pulse-text': step.status === 'in_progress',
                    'fw-semibold text-danger': step.status === 'failed',
                    'text-body': step.status === 'completed',
                    'text-muted': step.status === 'pending' || step.status === 'skipped'
                  }"
                >
                  {{ step.label }}
                </span>

                <span
                  v-if="formatRelativeTime(step.timestamp)"
                  class="text-muted small timestamp ms-auto font-monospace"
                >
                  {{ formatRelativeTime(step.timestamp) }}
                </span>
              </div>

              <!-- Error Message if Present -->
              <div v-if="step.error" class="error-message text-danger small mt-1 d-flex align-items-start gap-1">
                <i class="bi bi-exclamation-circle-fill flex-shrink-0 mt-1"></i>
                <span>{{ step.error }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'ProgressTracker',
  props: {
    steps: {
      type: Array,
      default: () => []
    },
    // 'creation', 'reset', 'resetUsers', or null
    activeOperation: {
      type: String,
      default: null
    },
    // 'in_progress', 'completed', 'failed', or null
    provisioningState: {
      type: String,
      default: null
    }
  },
  setup(props) {
    // Collapsed state: default to true when completed, false otherwise
    const isCollapsed = ref(props.provisioningState === 'completed');

    // Keep collapsed state in sync if provisioningState transitions
    watch(
      () => props.provisioningState,
      (newState, oldState) => {
        if (newState === 'completed' && oldState !== 'completed') {
          isCollapsed.value = true;
        } else if (newState === 'in_progress' || newState === 'failed') {
          isCollapsed.value = false;
        }
      }
    );

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    // Derive readable operation header title
    const operationHeaderTitle = computed(() => {
      switch (props.activeOperation) {
        case 'creation':
          return 'Creating Demo Session';
        case 'reset':
          return 'Resetting Demo';
        case 'resetUsers':
          return 'Resetting User Accounts';
        default:
          if (props.provisioningState === 'failed') {
            return 'Operation Failed';
          }
          if (props.provisioningState === 'completed') {
            return 'Operation Completed';
          }
          return 'Operation Progress';
      }
    });

    // Derive completed badge / alert message
    const completedMessage = computed(() => {
      switch (props.activeOperation) {
        case 'reset':
          return 'Demo reset successfully';
        case 'resetUsers':
          return 'User accounts reset successfully';
        case 'creation':
        default:
          return 'Demo session created successfully';
      }
    });

    /**
     * Converts a Firestore Timestamp, Date, or numeric millisecond timestamp to relative time (e.g., "3s ago")
     */
    const formatRelativeTime = (timestamp) => {
      if (!timestamp) return null;

      let date;
      if (typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp.seconds === 'number') {
        date = new Date(timestamp.seconds * 1000);
      } else {
        return null;
      }

      if (isNaN(date.getTime())) return null;

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSec = Math.max(0, Math.floor(diffMs / 1000));

      if (diffSec < 5) {
        return 'just now';
      }
      if (diffSec < 60) {
        return `${diffSec}s ago`;
      }
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) {
        return `${diffMin}m ago`;
      }
      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) {
        return `${diffHours}h ago`;
      }
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    };

    return {
      isCollapsed,
      toggleCollapse,
      operationHeaderTitle,
      completedMessage,
      formatRelativeTime
    };
  }
};
</script>

<style scoped>
.progress-tracker-container {
  width: 100%;
}

.progress-tracker-card {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 0.125rem 0.375rem rgba(0, 0, 0, 0.05) !important;
}

.bg-success-subtle-custom {
  background-color: #f0fdf4;
}

.header-title {
  font-size: 0.95rem;
  letter-spacing: 0.01em;
}

.toggle-btn {
  font-size: 0.8rem;
  padding: 0.25rem 0.6rem;
  border-radius: 0.375rem;
}

/* Timeline Layout */
.timeline {
  position: relative;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  position: relative;
  min-height: 2.25rem;
}

/* Connecting vertical line */
.timeline-item::before {
  content: '';
  position: absolute;
  top: 1.15rem;
  left: 0.55rem;
  bottom: -0.35rem;
  width: 2px;
  background-color: #e9ecef;
  z-index: 1;
}

.timeline-item.is-last::before {
  display: none;
}

/* Timeline Icon */
.timeline-icon-wrapper {
  position: relative;
  z-index: 2;
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 50%;
  margin-right: 0.85rem;
  margin-top: 0.1rem;
}

.timeline-icon {
  font-size: 1rem;
  line-height: 1;
}

.timeline-spinner {
  width: 0.95rem;
  height: 0.95rem;
  border-width: 0.15em;
  animation: spinner-border 0.75s linear infinite, subtle-pulse 1.8s ease-in-out infinite;
}

/* Step content */
.timeline-content {
  flex-grow: 1;
  padding-left: 0.25rem;
}

.step-label {
  font-size: 0.9rem;
  transition: color 0.2s ease-in-out;
}

.timestamp {
  font-size: 0.75rem;
}

.error-message {
  font-size: 0.8rem;
  line-height: 1.3;
}

/* In-progress pulse effect */
.is-in-progress .timeline-content {
  position: relative;
}

.active-pulse-text {
  animation: text-pulse 2s ease-in-out infinite;
}

@keyframes text-pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.75;
  }
  100% {
    opacity: 1;
  }
}

@keyframes subtle-pulse {
  0% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(13, 110, 253, 0));
  }
  50% {
    transform: scale(1.12);
    filter: drop-shadow(0 0 4px rgba(13, 110, 253, 0.45));
  }
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(13, 110, 253, 0));
  }
}

/* Highlight failed row */
.timeline-item.is-failed .timeline-content {
  background-color: rgba(220, 53, 69, 0.04);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem 0.5rem 0.5rem;
  margin-left: -0.25rem;
}

/* Highlight in-progress row */
.timeline-item.is-in-progress .timeline-content {
  background-color: rgba(13, 110, 253, 0.03);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem 0.5rem 0.5rem;
  margin-left: -0.25rem;
}
</style>
